import React, { useEffect, useState } from "react";
import { Invoice } from "../../interfaces/invoice";
import {
  generateInvoiceReport,
  saveInvoiceReport,
} from "../../services/invoiceServices";

interface GenerateInvoiceProps {
  invoices: Invoice[];
  onClose: (message?: string) => void;
}

const GenerateInvoice: React.FC<GenerateInvoiceProps> = ({
  invoices,
  onClose,
}) => {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  useEffect(() => {
    const startInvoiceReport = async () => {
      try {
        setLoadingSpinner(true);
        if (invoices) {
          const responseUrl = await generateInvoiceReport(invoices);
          if (responseUrl) {
            setPdfBlob(responseUrl);
            const pdfUrl = URL.createObjectURL(responseUrl);
            setPdfUrl(pdfUrl);
          } else {
            throw new Error("Failed to generate the report.");
          }
        }
      } catch (error) {
        setErrorMessage("Error while generating the final report.");
      } finally {
        setLoadingSpinner(false);
      }
    };

    startInvoiceReport();
  }, [invoices]);

  const handleSaveInvoiceReport = async () => {
    if (!pdfBlob) {
      setErrorMessage("Please generate the report before sending.");
      return;
    }
    try {
      setLoadingSpinner(true);
      let response;
      if (invoices) {
        //take the report and save it in blob and mark all those invoices as billed
        response = await saveInvoiceReport(invoices, pdfBlob);
      }

      if (response) {
        onClose();
      } else {
        throw new Error("Failed to save invoice report.");
      }
    } catch (error) {
      setErrorMessage("Failed to save invoice report.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (!pdfBlob) {
      setErrorMessage("No CSV report available.");
      return;
    }

    const csvUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = csvUrl;
    link.download = "invoice_report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Save Invoice Report</h2>
          <div>
            The Generated Report is a preview of the invoice report. Upon
            saving, all orders on this list will be marked as billed.
          </div>
          <div className="error">
            Download and save the CSV file before clicking Save Report.
          </div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="field-long" style={{ flex: "0 0 100%" }}>
            <label className="field-label">Generated Report:</label>
            <div className="field-input">
              {pdfUrl ? (
                <>
                  <button
                    className="smbtn btn-blue"
                    onClick={handleDownloadCsv}
                  >
                    Download CSV
                  </button>
                </>
              ) : (
                <p>Generating report...</p>
              )}
            </div>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
        <div className="filter-actions">
          <button className="smbtn btn-grey" onClick={() => onClose()}>
            Cancel
          </button>
          <button
            className={`smbtn btn-green`}
            onClick={handleSaveInvoiceReport}
            disabled={!pdfUrl}
          >
            Save Report
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default GenerateInvoice;
